import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Loader2, Trash2, Megaphone, DollarSign, LinkIcon, Users, Check, ExternalLink, ArrowLeft, Send, ImagePlus, MessageSquarePlus, Pencil, Heart, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AppHeader } from "../components/AppHeader";
import { BottomSheet } from "../components/BottomSheet";
import { MediaUploader } from "../components/MediaUploader";
import { LocationAutocomplete } from "../components/LocationAutocomplete";
import { StripeCheckoutSheet } from "../components/StripeCheckoutSheet";
import { Poll } from "../components/Poll";
import { PollComposer, pollPayload } from "../components/PollComposer";
import { parseMentions } from "../lib/mentions";
import { MentionInput } from "../components/MentionInput";
import { Fab, Empty, Avatar } from "./Hangouts";

const fmtDate = (d) => { try { return format(new Date(d), "EEE, MMM d, yyyy"); } catch { return d; } };
const ago = (d) => { try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return ""; } };
const inputStyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" };
const cls = "w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)] min-w-0";

const bands = [
  { key: "senior", label: "Senior Adult (65+)" },
  { key: "adult", label: "Adult (18–64)" },
  { key: "teenager", label: "Teenager (13–17)" },
  { key: "child", label: "Child (2–12)" },
  { key: "infant", label: "Infant (0–2)" },
];

const emptyForm = {
  title: "", content: "", image: "", event_date: "", location: "", cost: "",
  links: [{ name: "", url: "" }, { name: "", url: "" }, { name: "", url: "" }], useAge: false,
  age_costs: { senior: "", adult: "", teenager: "", child: "", infant: "" },
  usePoll: false, pollQuestions: [{ type: "mc", text: "", options: ["", ""] }],
};

export default function Reunions() {
  const { family, user } = useAuth();
  const canPost = family?.role === "admin" || family?.role === "organizer";
  const canPollReunion = family?.can_poll_reunion;
  const reunionFee = family?.reunion_fee ?? 0;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [posting, setPosting] = useState(false);
  const [rsvping, setRsvping] = useState(null);
  const [checkout, setCheckout] = useState(null); // RSVP checkout
  const [feeCheckout, setFeeCheckout] = useState(null); // reunion creation fee checkout
  const [active, setActive] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [members, setMembers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => { api.get("/families/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const o = searchParams.get("open");
    if (o) { setActive(o); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);

  const openEdit = (it, e) => {
    e?.stopPropagation();
    setForm({
      title: it.title || "", content: it.content || "", image: it.image || "",
      event_date: it.event_date || "", location: it.location || "", cost: it.cost != null ? String(it.cost) : "",
      links: [0, 1, 2].map((i) => (it.links && it.links[i]) ? { name: it.links[i].name || "", url: it.links[i].url || "" } : { name: "", url: "" }),
      useAge: !!(it.age_costs && Object.keys(it.age_costs).length),
      age_costs: {
        senior: it.age_costs?.senior != null ? String(it.age_costs.senior) : "",
        adult: it.age_costs?.adult != null ? String(it.age_costs.adult) : "",
        teenager: it.age_costs?.teenager != null ? String(it.age_costs.teenager) : "",
        child: it.age_costs?.child != null ? String(it.age_costs.child) : "",
        infant: it.age_costs?.infant != null ? String(it.age_costs.infant) : "",
      },
      usePoll: !!it.poll,
      pollQuestions: it.poll?.questions?.map((q) => ({ type: q.type, text: q.text, options: q.options?.length ? q.options : ["", ""] })) || [{ type: "mc", text: "", options: ["", ""] }],
    });
    setEditingId(it.id);
    setComposer(true);
  };

  const load = useCallback(async () => {
    try { const { data } = await api.get("/reunions"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and details required"); return; }
    setPosting(true);
    try {
      const payload = {
        title: form.title, content: form.content, image: form.image || null,
        event_date: form.event_date || null, location: form.location || null,
        cost: form.cost ? parseFloat(form.cost) : null,
        links: form.links.filter((l) => l.url && l.url.trim()).map((l) => ({ name: (l.name || "").trim(), url: l.url.trim() })).slice(0, 3),
        age_costs: null,
      };
      if (form.useAge) {
        const ac = {};
        bands.forEach((b) => { if (form.age_costs[b.key] !== "") ac[b.key] = parseFloat(form.age_costs[b.key]); });
        payload.age_costs = ac;
      }
      if (canPollReunion && form.usePoll) {
        const p = pollPayload(form.pollQuestions);
        if (p) payload.poll = p;
      }
      if (editingId) {
        await api.patch(`/reunions/${editingId}`, payload);
        toast.success("Announcement updated");
        setForm(emptyForm); setComposer(false); setEditingId(null); await load();
      } else {
        payload.origin_url = window.location.origin;
        const { data } = await api.post("/reunions", payload);
        if (data.status === "payment_required") {
          setFeeCheckout({ client_secret: data.client_secret, session_id: data.session_id });
          return;
        }
        toast.success("Announcement posted");
        setForm(emptyForm); setComposer(false); setEditingId(null); await load();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    try { await api.delete(`/reunions/${id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doRsvp = async (it, e) => {
    e?.stopPropagation();
    setRsvping(it.id);
    try {
      const { data } = await api.post(`/reunions/${it.id}/rsvp`, { origin_url: window.location.origin });
      if (data.status === "payment_required") {
        setCheckout({ client_secret: data.client_secret, session_id: data.session_id });
        return;
      }
      toast.success("You're going! RSVP confirmed");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRsvping(null); }
  };

  const likeReunion = async (it) => {
    try {
      const { data } = await api.post("/likes/toggle", { kind: "reunion", id: it.id });
      setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, liked: data.liked, like_count: data.like_count } : x));
    } catch {}
  };

  const feeLabel = (it) => {
    if (it.age_costs && Object.keys(it.age_costs).length) return "Priced by age group";
    if (it.cost) return `$${Number(it.cost).toFixed(2)} per person`;
    return "Free to attend";
  };

  if (active) return <ReunionThread reunionId={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div>
      <AppHeader title="Reunions" />
      <div className="px-5 py-5 lg:px-8 lg:py-8 lg:max-w-4xl lg:mx-auto space-y-4 lg:space-y-5">
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin gold-text" /></div>
        ) : items.length === 0 ? (
          <Empty text={canPost ? "No announcements yet. Tap + to post one." : "No reunion announcements yet."} />
        ) : items.map((it) => (
          <motion.div key={it.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} whileTap={{ scale: 0.995 }}
            onClick={() => setActive(it.id)}
            data-testid={`reunion-${it.id}`} className="card-surface rounded-2xl overflow-hidden cursor-pointer">
            {it.image && <img src={fileUrl(it.image)} alt="" className="w-full h-44 object-cover" />}
            <div className="p-4">
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest gold-text mb-2"
                style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 30%, transparent)" }}>
                <Megaphone size={11} /> Announcement
              </span>
              <h3 className="font-serif-display text-2xl leading-tight">{it.title}</h3>
              <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{it.content}</p>

              {it.poll && <div onClick={(e) => e.stopPropagation()}><Poll poll={it.poll} kind="reunion" id={it.id} userId={user?.id} testidPrefix={`reunion-poll-${it.id}`} collapsed /></div>}

              <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
                {it.event_date && <Info icon={Calendar} text={fmtDate(it.event_date)} />}
                {it.location && <Info icon={MapPin} text={it.location} />}
                <Info icon={DollarSign} text={feeLabel(it)} />
                <Info icon={Users} text={`${it.rsvp_count || 0} going`} />
              </div>

              {it.age_costs && Object.keys(it.age_costs).length > 0 && (
                <div className="mt-3 rounded-xl p-3" style={{ background: "var(--overlay)" }}>
                  <div className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: "var(--text-muted)" }}>Pricing</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {bands.filter((b) => it.age_costs[b.key] != null).map((b) => (
                      <div key={b.key} className="flex justify-between text-xs">
                        <span style={{ color: "var(--text-secondary)" }}>{b.label}</span>
                        <span className="font-semibold">${Number(it.age_costs[b.key]).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {it.links && it.links.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {it.links.map((l, i) => {
                    const url = (l.url || "").startsWith("http") ? l.url : `https://${l.url}`;
                    return (
                      <a key={i} href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        data-testid={`reunion-link-${it.id}-${i}`}
                        className="flex items-center gap-2 text-xs truncate">
                        <ExternalLink size={13} className="gold-text shrink-0" />
                        <span className="truncate">
                          {l.name ? <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{l.name}: </span> : null}
                          <span className="gold-text">{l.url}</span>
                        </span>
                      </a>
                    );
                  })}
                </div>
              )}

              <div className="mt-4">
                {it.my_rsvp && it.my_rsvp.paid ? (
                  <div data-testid={`rsvp-confirmed-${it.id}`}
                    className="w-full rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)", color: "var(--accent)" }}>
                    <Check size={16} /> You're going{it.my_rsvp.fee ? ` · paid $${Number(it.my_rsvp.fee).toFixed(2)}` : ""}
                  </div>
                ) : (
                  <motion.button whileTap={{ scale: 0.97 }} onClick={(e) => doRsvp(it, e)} disabled={rsvping === it.id}
                    data-testid={`rsvp-btn-${it.id}`}
                    className="w-full rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
                    {rsvping === it.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} RSVP
                  </motion.button>
                )}
              </div>

              <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid var(--hairline)" }}>
                <button onClick={(e) => { e.stopPropagation(); setActive(it.id); }} data-testid={`reunion-comment-btn-${it.id}`}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ background: "var(--overlay)" }}>
                  <MessageSquarePlus size={15} style={{ color: "var(--text-secondary)" }} />
                  <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>Comment ({it.reply_count || 0})</span>
                </button>
                <button onClick={(e) => { e.stopPropagation(); likeReunion(it); }} data-testid={`reunion-like-${it.id}`}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ background: "var(--overlay)" }}>
                  <Heart size={15} fill={it.liked ? "var(--accent)" : "none"} style={{ color: it.liked ? "var(--accent)" : "var(--text-secondary)" }} />
                  <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>{it.like_count || 0}</span>
                </button>
                <div className="flex items-center gap-3">
                  <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>By {it.author?.name}</span>
                  {(it.author?.id === user?.id || canPost) && (
                    <button onClick={(e) => openEdit(it, e)} data-testid={`reunion-edit-${it.id}`}>
                      <Pencil size={15} className="gold-text" />
                    </button>
                  )}
                  {canPost && (
                    <button onClick={(e) => remove(it.id, e)} data-testid={`reunion-delete-${it.id}`}>
                      <Trash2 size={15} style={{ color: "var(--destructive)" }} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {canPost && <Fab onClick={() => setComposer(true)} testid="new-reunion-fab" />}

      <StripeCheckoutSheet open={!!checkout} clientSecret={checkout?.client_secret} sessionId={checkout?.session_id}
        onClose={() => { setCheckout(null); load(); }}
        onPaid={() => { setCheckout(null); toast.success("RSVP confirmed & paid"); load(); }} />

      <StripeCheckoutSheet open={!!feeCheckout} clientSecret={feeCheckout?.client_secret} sessionId={feeCheckout?.session_id}
        onClose={() => { setFeeCheckout(null); setPosting(false); load(); }}
        onPaid={() => { setFeeCheckout(null); setPosting(false); setComposer(false); setEditingId(null); setForm(emptyForm); toast.success("Reunion published!"); load(); }} />

      <BottomSheet open={composer} onClose={() => { setComposer(false); setEditingId(null); setForm(emptyForm); }} title={editingId ? "Edit Announcement" : "New Announcement"}>
        <div className="space-y-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Title (e.g. Summer Family Reunion)" data-testid="reunion-title-input" className={cls} style={inputStyle} />
          <MentionInput as="textarea" value={form.content} onChange={(v) => setForm({ ...form, content: v })} members={members} rows={3}
            placeholder="Details… use @ to tag" testid="reunion-content-input" className={cls} style={{ ...inputStyle, fontSize: 16 }} />
          <div className="min-w-0">
            <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Date</label>
            <input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              data-testid="reunion-date-input" className={cls} style={inputStyle} />
          </div>
          <div className="min-w-0">
            <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Location</label>
            <LocationAutocomplete value={form.location} onChange={(v) => setForm((f) => ({ ...f, location: v }))} testid="reunion-location-input" />
          </div>

          <div className="min-w-0">
            <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Cost (USD)</label>
            <input type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })}
              placeholder="0.00 — leave blank for free" data-testid="reunion-cost-input" className={cls} style={inputStyle} />
          </div>

          <label className="flex items-center justify-between rounded-xl px-4 py-3" style={inputStyle}>
            <span className="text-sm">Charge different prices by age group</span>
            <input type="checkbox" checked={form.useAge} onChange={(e) => setForm({ ...form, useAge: e.target.checked })}
              data-testid="reunion-useage-toggle" className="h-4 w-4 accent-[var(--accent)]" />
          </label>

          {form.useAge && (
            <div className="space-y-2 rounded-xl p-3" style={{ background: "var(--overlay)" }}>
              {bands.map((b) => (
                <div key={b.key} className="flex items-center gap-2">
                  <span className="text-xs flex-1" style={{ color: "var(--text-secondary)" }}>{b.label}</span>
                  <div className="flex items-center gap-1 rounded-lg px-2" style={inputStyle}>
                    <DollarSign size={12} style={{ color: "var(--text-muted)" }} />
                    <input type="number" min="0" step="0.01" value={form.age_costs[b.key]}
                      onChange={(e) => setForm({ ...form, age_costs: { ...form.age_costs, [b.key]: e.target.value } })}
                      placeholder="0.00" data-testid={`reunion-agecost-${b.key}`}
                      className="w-20 bg-transparent py-2 text-sm outline-none" style={{ color: "var(--text-primary)" }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wide flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
              <LinkIcon size={11} /> Links (up to 3)
            </label>
            <div className="space-y-2 mt-1">
              {form.links.map((l, i) => (
                <div key={i} className="grid grid-cols-2 gap-2">
                  <input value={l.name}
                    onChange={(e) => { const nl = [...form.links]; nl[i] = { ...nl[i], name: e.target.value }; setForm({ ...form, links: nl }); }}
                    placeholder="Name" data-testid={`reunion-link-name-${i}`} className={cls} style={inputStyle} />
                  <input value={l.url}
                    onChange={(e) => { const nl = [...form.links]; nl[i] = { ...nl[i], url: e.target.value }; setForm({ ...form, links: nl }); }}
                    placeholder="https://…" data-testid={`reunion-link-url-${i}`} className={cls} style={inputStyle} />
                </div>
              ))}
            </div>
          </div>

          {form.image && <img src={fileUrl(form.image)} alt="" className="rounded-xl w-full max-h-48 object-cover" />}
          <MediaUploader label="Cover photo" testid="reunion-image-upload" onUploaded={(p) => setForm((f) => ({ ...f, image: p }))} />

          {canPollReunion && (
            <div className="rounded-xl p-3" style={{ background: "var(--overlay)" }}>
              <label className="flex items-center justify-between">
                <span className="text-sm">Attach a poll (Legacy)</span>
                <input type="checkbox" checked={form.usePoll} onChange={(e) => setForm({ ...form, usePoll: e.target.checked })}
                  data-testid="reunion-poll-toggle" className="h-4 w-4 accent-[var(--accent)]" />
              </label>
              {form.usePoll && (
                <div className="mt-3">
                  <PollComposer questions={form.pollQuestions} onChange={(qs) => setForm({ ...form, pollQuestions: qs })} />
                </div>
              )}
            </div>
          )}

          {!editingId && reunionFee > 0 && (
            <div className="rounded-xl px-4 py-3 text-xs flex items-center gap-2" data-testid="reunion-fee-note"
              style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--text-secondary)" }}>
              <DollarSign size={14} className="gold-text" />
              Your {family?.tier} plan has a ${reunionFee.toFixed(0)} reunion creation fee, paid securely at checkout.
            </div>
          )}

          <motion.button whileTap={{ scale: 0.97 }} onClick={submit} disabled={posting}
            data-testid="reunion-submit-btn"
            className="w-full rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
            style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
            {posting && <Loader2 size={16} className="animate-spin" />}
            {editingId ? "Save Changes" : (reunionFee > 0 ? `Pay $${reunionFee.toFixed(0)} & Post` : "Post Announcement")}
          </motion.button>
        </div>
      </BottomSheet>
    </div>
  );
}

function Info({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-primary)" }}>
      <Icon size={13} className="gold-text" /> {text}
    </div>
  );
}

export function ReunionThread({ reunionId, onBack }) {
  const { user, family } = useAuth();
  const canPoll = family?.can_poll;
  const [item, setItem] = useState(null);
  const [reply, setReply] = useState("");
  const [replyImage, setReplyImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [replyPoll, setReplyPoll] = useState(false);
  const [pollQuestions, setPollQuestions] = useState([{ type: "mc", text: "", options: ["", ""] }]);
  const fileRef = useRef();

  const load = useCallback(async () => {
    const { data } = await api.get(`/reunions/${reunionId}`);
    setItem(data);
  }, [reunionId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/families/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    document.body.classList.add("hide-bottom-nav");
    return () => document.body.classList.remove("hide-bottom-nav");
  }, []);

  const likeReply = async (r) => {
    try {
      const { data } = await api.post("/likes/toggle", { kind: "reunion_reply", id: r.id });
      setItem((it) => ({ ...it, replies: it.replies.map((x) => x.id === r.id ? { ...x, liked: data.liked, like_count: data.like_count } : x) }));
    } catch {}
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReplyImage(data.path);
      toast.success("Image attached");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const send = async () => {
    if (!reply.trim() && !replyImage && !replyPoll) return;
    setSending(true);
    try {
      const payload = { content: reply.trim(), image: replyImage || null, mentions: parseMentions(reply, members) };
      if (canPoll && replyPoll) { const p = pollPayload(pollQuestions); if (p) payload.poll = p; }
      await api.post(`/reunions/${reunionId}/replies`, payload);
      setReply(""); setReplyImage(""); setReplyPoll(false);
      setPollQuestions([{ type: "mc", text: "", options: ["", ""] }]);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col" style={{ height: "100dvh" }}>
      <div className="shrink-0 z-40 glass hairline px-5 py-4 flex items-center gap-3">
        <button onClick={onBack} data-testid="reunion-thread-back-btn"><ArrowLeft size={22} /></button>
        <h1 className="font-serif-display text-2xl">Announcement</h1>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
      {!item ? <div className="grid place-items-center py-20"><Loader2 className="animate-spin gold-text" /></div> : (
        <div className="px-5 py-5">
          <div className="card-surface rounded-2xl overflow-hidden">
            {item.image && <img src={fileUrl(item.image)} alt="" className="w-full h-44 object-cover" />}
            <div className="p-4">
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest gold-text mb-2"
                style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
                <Megaphone size={11} /> Announcement
              </span>
              <h3 className="font-serif-display text-2xl leading-tight">{item.title}</h3>
              <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{item.content}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
                {item.event_date && <Info icon={Calendar} text={fmtDate(item.event_date)} />}
                {item.location && <Info icon={MapPin} text={item.location} />}
              </div>
            </div>
          </div>

          <div className="mt-5 mb-2 text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
            {item.replies.length} {item.replies.length === 1 ? "Comment" : "Comments"}
          </div>
          <div className="space-y-3 pl-3" style={{ borderLeft: "1px solid rgba(124,131,255,0.25)" }}>
            {item.replies.map((r) => (
              <motion.div key={r.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                data-testid={`reunion-reply-${r.id}`} className="rounded-xl p-3" style={{ background: "var(--overlay)" }}>
                <div className="flex items-center gap-2">
                  <Avatar name={r.author?.name} sm />
                  <span className="text-xs font-semibold">{r.author?.name}</span>
                  <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{ago(r.created_at)}</span>
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap">{r.content}</p>
                {r.image && <img src={fileUrl(r.image)} alt="" className="mt-2 rounded-lg w-full max-h-60 object-cover" />}
                {r.poll && <Poll poll={r.poll} kind="reunion_reply" id={r.id} userId={user?.id} testidPrefix={`reunion-reply-poll-${r.id}`} collapsed />}
                <button onClick={() => likeReply(r)} data-testid={`reunion-reply-like-${r.id}`} className="flex items-center gap-1.5 mt-2">
                  <Heart size={14} fill={r.liked ? "var(--accent)" : "none"} style={{ color: r.liked ? "var(--accent)" : "var(--text-muted)" }} />
                  <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{r.like_count || 0}</span>
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      )}
      </div>
      <div className="shrink-0 glass z-[60]" style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px))" }}>
        {replyPoll && (
          <div className="px-3 pt-3 max-h-72 overflow-y-auto no-scrollbar">
            <PollComposer questions={pollQuestions} onChange={setPollQuestions} />
          </div>
        )}
        {replyImage && (
          <div className="px-3 pt-2 flex items-center gap-2">
            <img src={fileUrl(replyImage)} alt="" className="h-14 w-14 rounded-lg object-cover" />
            <button onClick={() => setReplyImage("")} className="text-xs" style={{ color: "var(--destructive)" }}>Remove</button>
          </div>
        )}
        <div className="p-3 flex items-end gap-2">
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="reunion-reply-image-file" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="reunion-reply-image-btn"
            className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--overlay)" }}>
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} style={{ color: "var(--text-secondary)" }} />}
          </button>
          {canPoll && (
            <button onClick={() => setReplyPoll((v) => !v)} data-testid="reunion-reply-poll-btn"
              className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: replyPoll ? "var(--accent)" : "var(--overlay)" }}>
              <BarChart3 size={18} style={{ color: replyPoll ? "#fff" : "var(--text-secondary)" }} />
            </button>
          )}
          <MentionInput value={reply} onChange={setReply} members={members} onEnter={send} wrapperClassName="flex-1"
            placeholder="Write a comment… use @ to tag" testid="reunion-reply-input"
            className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
          <button onClick={send} disabled={sending} data-testid="reunion-reply-send-btn"
            className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
